import { useEffect } from 'react'
import { SiteNetworkView } from './SiteNetworkView'

interface Props {
  siteId: string
  /** Device ID to focus on (switch ID — will be pre-selected in switch tabs) */
  deviceId: string
  /** Optional port number to highlight (e.g. when navigating from a wired client) */
  highlightPort?: number | null
  onClose: () => void
  /** Forward device navigation from the network view */
  onOpenDevice?: (slug: string) => void
}

/**
 * Fullscreen modal overlay that shows the SiteNetworkView focused on a specific device.
 * Used when clicking a device in: clients tab, topology, devices tab.
 */
export function NetworkDeviceModal({ siteId, deviceId, highlightPort, onClose, onOpenDevice }: Props) {
  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  // Escape to close
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 90,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
        }}
      />
      {/* Modal panel */}
      <div
        style={{
          position: 'fixed', top: 24, left: 24, right: 24, bottom: 24,
          zIndex: 91, background: 'var(--bg-base)',
          borderRadius: 16, border: '1px solid var(--border)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          animation: 'ndmSlideIn 0.25s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 12, right: 12, zIndex: 5,
            width: 32, height: 32, borderRadius: 8,
            border: '1px solid var(--border-subtle)',
            background: 'var(--bg-surface)',
            color: 'var(--text-secondary)', fontSize: 16,
            cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}
          aria-label="Close"
        >
          ✕
        </button>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <SiteNetworkView
            siteId={siteId}
            onOpenDevice={onOpenDevice}
            initialSwitchId={deviceId}
            highlightPort={highlightPort ?? undefined}
          />
        </div>
      </div>

      <style>{`
        @keyframes ndmSlideIn {
          from { opacity: 0; transform: scale(0.96) translateY(12px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </>
  )
}
